import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../stores/authStore';
import { getApiBaseUrl } from '../services/api';

export interface MobileNavConfig {
  showMobileFooter?: boolean;
  mobileTheme?: 'modern-pill' | 'classic' | 'minimal';
  enableMobileFab?: boolean;
  showAttemptsTab?: boolean;
  showStudioTab?: boolean;
  showReportsTab?: boolean;
}

export interface TenantBranding {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  loginLogoUrl: string | null;
  topNavLogoUrl: string | null;
  primaryColor: string;
  directLoginUrl: string | null;
  mobileNavConfig?: MobileNavConfig;
  governancePolicy?: {
    learnerCanViewOwnResultReport?: boolean;
    [key: string]: any;
  };
}

const brandingCache = new Map<string, TenantBranding>();

export function useTenantBranding(explicitSlug?: string) {
  const { user } = useAuth();
  const slug = explicitSlug || user?.tenantSlug;

  const [branding, setBranding] = useState<TenantBranding | null>(() => {
    if (slug && brandingCache.has(slug)) {
      return brandingCache.get(slug)!;
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBranding = useCallback(async (targetSlug: string) => {
    if (!targetSlug) return;
    if (brandingCache.has(targetSlug)) {
      setBranding(brandingCache.get(targetSlug)!);
    }
    setIsLoading(true);
    setError(null);
    try {
      const apiBase = getApiBaseUrl();
      const res = await fetch(`${apiBase}/api/v1/auth/tenant/${encodeURIComponent(targetSlug)}/branding`);
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.data) {
          brandingCache.set(targetSlug, json.data);
          setBranding(json.data);
        }
      } else if (res.status === 404) {
        setError('Tenant organization not found.');
      } else {
        setError('Unable to load organization branding.');
      }
    } catch {
      setError('Network error loading organization branding.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (slug) {
      fetchBranding(slug);
    }
  }, [slug, fetchBranding]);

  return {
    branding,
    isLoading,
    error,
    refresh: () => slug && fetchBranding(slug),
  };
}
